import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  debounce,
  fetchData,
  formatDate,
  formatNumber,
  toasterrormsg,
  useMenuIdPermissions,
} from '../../../reusable'
import { KTIcon, toAbsoluteUrl } from '../../../_metronic/helpers'
import { ToastContainer } from 'react-toastify'
import DataTable from 'react-data-table-component'
import Loader from '../components/Loader'
import { Tr } from '../../../_metronic/i18n/Tr'
import { Modal } from 'react-bootstrap'

const HISTORY_PAGE_SIZE = 15

const AdsImpressions: FC = () => {
  const t = Tr()
  const { menuId } = useMenuIdPermissions()

  // Impression report state
  const [reportList, setReportList] = useState<any[]>([])
  const [inputValue, setInputValue] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [totalRecords, setTotalRecords] = useState(0)
  const [limit, setLimit] = useState(10)
  const [offset, setOffset] = useState(0)
  const [sortField, setSortField] = useState('')
  const [sortOrder, setSortOrder] = useState('')
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [focus, setFocus] = useState('')

  const [showUsersModal, setShowUsersModal] = useState(false)
  const [selectedAd, setSelectedAd] = useState<any>(null)
  const [usersList, setUsersList] = useState<any[]>([])
  const [imageBaseUrl, setImageBaseUrl] = useState('')
  const [usersLoading, setUsersLoading] = useState(false)
  const [usersTotal, setUsersTotal] = useState(0)
  const [usersLimit, setUsersLimit] = useState(10)
  const [usersOffset, setUsersOffset] = useState(0)
  const [usersTerm, setUsersTerm] = useState('')

  const [selectedUser, setSelectedUser] = useState<any>(null)
  const [userHistory, setUserHistory] = useState<any[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false)
  const [historyTotal, setHistoryTotal] = useState(0)
  const [historyOffset, setHistoryOffset] = useState(0)
  const [historyHasMore, setHistoryHasMore] = useState(false)

  const historyScrollRef = useRef<HTMLDivElement | null>(null)

  const fetchImpressionReport = async () => {
    try {
      if (!isDataLoaded) setIsLoading(true)
      const param = {
        limit,
        offset,
        term: searchTerm.trim(),
        sortBy: sortField,
        sort: sortOrder,
      }
      const response = await fetchData('admin/ads/impression/report', param)
      if (response.status == '200') {
        const data = response?.data ?? {}
        setTotalRecords(data.totalRecord || 0)
        setReportList(data.list || [])
        setIsDataLoaded(true)
      } else {
        toasterrormsg(response?.message || 'An error occurred')
        setReportList([])
        setTotalRecords(0)
      }
    } catch (error) {
      console.error('Error fetching impression report:', error)
      toasterrormsg('Failed to fetch impression report')
      setReportList([])
      setTotalRecords(0)
    } finally {
      setIsDataLoaded(true)
      setIsLoading(false)
    }
  }

  // ── View Users API ──
  const fetchViewUsers = async (adsId: number, lmt = usersLimit, off = usersOffset, term = usersTerm) => {
    try {
      setUsersLoading(true)
      const param = { adsId, limit: lmt, offset: off, term: term.trim() }
      const response = await fetchData('admin/ads/views/list', param)
      if (response.status == '200') {
        const data = response?.data ?? {}
        setUsersTotal(data.totalRecord || 0)
        setUsersList(data.list || [])
        if (response.url) setImageBaseUrl(response.url)
      } else {
        toasterrormsg(response?.message || 'An error occurred')
        setUsersList([])
        setUsersTotal(0)
      }
    } catch (error) {
      console.error('Error fetching view users:', error)
      toasterrormsg('Failed to fetch ad view users')
      setUsersList([])
      setUsersTotal(0)
    } finally {
      setUsersLoading(false)
    }
  }

  // ── User History API (supports append for infinite scroll) ──
  const fetchUserHistory = async (
    adsId: number,
    userId: number,
    off: number,
    append: boolean = false
  ) => {
    try {
      if (append) {
        setHistoryLoadingMore(true)
      } else {
        setHistoryLoading(true)
      }
      const param = { adsId, userId, limit: HISTORY_PAGE_SIZE, offset: off }
      const response = await fetchData('admin/ads/views/user/history', param)
      if (response.status == '200') {
        const data = response?.data ?? {}
        const newList = data.list || []
        const total = data.totalRecord || 0
        setHistoryTotal(total)

        if (append) {
          setUserHistory((prev) => [...prev, ...newList])
        } else {
          setUserHistory(newList)
        }

        setHistoryOffset(off + newList.length)
        setHistoryHasMore(off + newList.length < total)
      } else {
        toasterrormsg(response?.message || 'An error occurred')
        if (!append) {
          setUserHistory([])
          setHistoryTotal(0)
          setHistoryHasMore(false)
        }
      }
    } catch (error) {
      console.error('Error fetching user history:', error)
      toasterrormsg('Failed to fetch user view history')
      if (!append) {
        setUserHistory([])
        setHistoryTotal(0)
        setHistoryHasMore(false)
      }
    } finally {
      setHistoryLoading(false)
      setHistoryLoadingMore(false)
    }
  }

  // ── Infinite scroll handler ──
  const handleHistoryScroll = useCallback(() => {
    const el = historyScrollRef.current
    if (!el || historyLoadingMore || !historyHasMore) return

    const { scrollTop, scrollHeight, clientHeight } = el
    // Trigger when scrolled within 100px of the bottom
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      if (selectedAd && selectedUser) {
        fetchUserHistory(selectedAd.adsId, selectedUser.userId, historyOffset, true)
      }
    }
  }, [historyLoadingMore, historyHasMore, historyOffset, selectedAd, selectedUser])

  // Attach scroll listener to modal body when in history view
  useEffect(() => {
    const el = historyScrollRef.current
    if (!el || !selectedUser) return

    el.addEventListener('scroll', handleHistoryScroll)
    return () => el.removeEventListener('scroll', handleHistoryScroll)
  }, [selectedUser, handleHistoryScroll])

  // Manual load more handler
  const loadMoreHistory = () => {
    if (!selectedAd || !selectedUser || historyLoadingMore || !historyHasMore) return
    fetchUserHistory(selectedAd.adsId, selectedUser.userId, historyOffset, true)
  }

  // ── Handlers ──
  const openUsersModal = (row: any) => {
    setSelectedAd(row)
    setSelectedUser(null)
    setUserHistory([])
    setUsersOffset(0)
    setUsersTerm('')
    setShowUsersModal(true)
    fetchViewUsers(row.adsId, usersLimit, 0, '')
  }

  const closeUsersModal = () => {
    setShowUsersModal(false)
    setSelectedAd(null)
    setSelectedUser(null)
    setUsersList([])
    setUserHistory([])
    setHistoryHasMore(false)
    setHistoryOffset(0)
  }

  const openUserHistory = (user: any) => {
    if (!selectedAd) return
    setSelectedUser(user)
    setUserHistory([])
    setHistoryOffset(0)
    setHistoryHasMore(false)
    fetchUserHistory(selectedAd.adsId, user.userId, 0, false)
  }

  const backToUsersList = () => {
    setSelectedUser(null)
    setUserHistory([])
    setHistoryHasMore(false)
    setHistoryOffset(0)
  }

  // ── Search ──
  const adSearch = (text: string, isfoc: any) => {
    setInputValue(text)
    setFocus(isfoc)
    debouncedSearch(text)
  }

  const debouncedSearch = useRef(
    debounce((value: string) => {
      setSearchTerm(value)
      setOffset(0)
    }, 500)
  ).current

  useEffect(() => {
    return () => debouncedSearch.cancel()
  }, [])

  // ── Sort ──
  const handleSort = (column: any, sortDirection: string) => {
    try {
      const fieldName = column.selector ? String(column.selector).split('.').pop() : ''
      setSortField(fieldName || '')
      setSortOrder(sortDirection === 'asc' ? 'asc' : 'desc')
    } catch (error) {
      console.error('Error in sorting:', error)
    }
  }

  // ── Pagination ──
  const handlePageChange = (newPage: number) => {
    const totalPages = Math.ceil(totalRecords / limit)
    if (newPage === 1) setOffset(0)
    else if (newPage === totalPages) setOffset((totalPages - 1) * limit)
    else setOffset((newPage - 1) * limit)
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setLimit(newRowsPerPage)
    setOffset(0)
  }

  const handleUsersPageChange = (newPage: number) => {
    if (!selectedAd) return
    const totalPages = Math.ceil(usersTotal / usersLimit)
    let newOffset = 0
    if (newPage === 1) newOffset = 0
    else if (newPage === totalPages) newOffset = (totalPages - 1) * usersLimit
    else newOffset = (newPage - 1) * usersLimit
    setUsersOffset(newOffset)
    fetchViewUsers(selectedAd.adsId, usersLimit, newOffset, usersTerm)
  }

  const handleUsersRowsPerPageChange = (newRowsPerPage: number) => {
    if (!selectedAd) return
    setUsersLimit(newRowsPerPage)
    setUsersOffset(0)
    fetchViewUsers(selectedAd.adsId, newRowsPerPage, 0, usersTerm)
  }

  // ── Table Search Header ──
  const CustomHeader = (props: any) => {
    const inputRef: any = useRef(null)
    const handleChange = async (e: any) => {
      await props.customSearch(e, 1)
    }
    useEffect(() => {
      if (inputRef.current && props.isfoc === 1) inputRef.current.focus()
    }, [props.isfoc])

    return (
      <div className='d-flex justify-content-between w-100 my-5'>
        <div className='d-flex align-items-center position-relative my-1'>
          <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
          <input
            type='text'
            data-kt-user-table-filter='search'
            className='form-control form-control-solid w-250px ps-14'
            placeholder={t('Search Ads')}
            value={props.search}
            onChange={(e: any) => handleChange(e.target.value)}
            ref={inputRef}
          />
        </div>
      </div>
    )
  }

  // ── Columns ──
  const columns = useMemo(
    () => [
      {
        name: t('Banner Title'),
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.title || '-'}</div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.title ,
        width: '200px',
      },
      {
        name: t('Placement'),
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.placementName || '-'}</div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.placementName,
        width: '200px',
      },
      {
        name: t('Duration'),
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {row.duration != null ? `${row.duration} ${row.durationType || ''}` : '-'}
            </div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.duration,
        width: '130px',
      },
      {
        name: t('Vendor'),
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.fullName || '-'}</div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.fullName,
        width: '200px',
      },
      {
        name: t('Impressions'),
        cell: (row: any) => (
          <div className='my-3'>
            <span className='imp-badge imp-badge--impressions'>
              <KTIcon iconName='eye' className='fs-4' />
              {row.viewCount || 0}
            </span>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.viewCount,
        width: '150px',
      },
      {
        name: t('Start Date'),
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-6'>
              {row.startDate ? formatDate(row.startDate, 'DD-MM-YYYY') : <span className='text-muted'>-</span>}
            </div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.startDate,
        width: '130px',
      },
      {
        name: t('End Date'),
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-6'>
              {row.endDate ? formatDate(row.endDate, 'DD-MM-YYYY') : <span className='text-muted'>-</span>}
            </div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.endDate,
        width: '130px',
      },
      {
        name: t('Payment Amount'),
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.price ? formatNumber(row.price, false) : '-'}</div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => Number(row.price),
        width: '160px',
      },
      {
        name: t('View Users'),
        cell: (row: any) => (
          <div className='my-3'>
            <button
              className='btn btn-light-info btn-sm d-flex align-items-center gap-1 px-3 py-2 rounded-pill'
              title='View Users'
              onClick={() => openUsersModal(row)}
            >
              <KTIcon iconName='people' className='fs-4' iconType='solid' />
              <span className='fs-7'>{t('View Users')}</span>
            </button>
          </div>
        ),
        sortable: false,
        width: '150px',
      },
    ],
    [t]
  )

  // ── Users modal columns ──
  const usersColumns = useMemo(
    () => [
      {
        name: t('User'),
        cell: (row: any) => (
          <div className='d-flex align-items-center gap-3 my-2'>
            {row.profile ? (
              <img
                src={imageBaseUrl + row.profile}
                alt={row.fullName || ''}
                className='imp-user-profile-img'
                onError={(e) => {
                  e.currentTarget.onerror = null
                  e.currentTarget.src = toAbsoluteUrl('/media/avatars/blank.png')
                }}
              />
            ) : (
              <div className='imp-user-avatar'>
                {(row.fullName || row.userName || 'U').charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <div className='fw-bold fs-6'>{row.fullName || row.userName || '-'}</div>
              {row.email && <div className='text-muted fs-8'>{row.email}</div>}
            </div>
          </div>
        ),
        sortable: false,
        grow: 2,
      },
      {
        name: t('View Count'),
        cell: (row: any) => (
          <div className='my-2'>
            <span className='imp-badge imp-badge--count'>
              <KTIcon iconName='eye' className='fs-5' />
              {row.viewCount || row.count || 0} {t('views')}
            </span>
          </div>
        ),
        sortable: false,
        width: '150px',
      },
      // {
      //   name: t('History'),
      //   cell: (row: any) => (
      //     <div className='my-2'>
      //       <button
      //         className='btn btn-light-primary btn-sm d-flex align-items-center gap-1 px-3 py-2 rounded-pill'
      //         onClick={() => openUserHistory(row)}
      //       >
      //         <KTIcon iconName='time' className='fs-4' iconType='solid' />
      //         <span className='fs-7'>{t('History')}</span>
      //       </button>
      //     </div>
      //   ),
      //   sortable: false,
      //   width: '130px',
      // },
    ],
    [t, selectedAd, imageBaseUrl]
  )

  // ── Effects ──
  useEffect(() => {
    fetchImpressionReport()
  }, [limit, offset, sortField, sortOrder, searchTerm])

  useEffect(() => {
    document.title = 'Insayr | Ads Impressions'
  }, [])

  return (
    <>
      <ToastContainer />
      {isLoading && <Loader />}

      {/* Info Banner */}
      <div className='imp-info-banner'>
        <KTIcon iconName='chart-simple' className='fs-1 text-primary flex-shrink-0' />
        <div>
          <div className='fw-bold fs-6 text-primary mb-1'>{t('Ad Impression Report')}</div>
          <p className='text-gray-700 fs-7 mb-0'>
            {t('View impression statistics for all advertisements. Click "View Users" to see which users viewed each ad.')}
          </p>
        </div>
      </div>

      {/* Main Table */}
      <DataTable
        columns={columns}
        data={reportList}
        pagination
        paginationServer
        onChangePage={handlePageChange}
        paginationTotalRows={totalRecords}
        paginationPerPage={limit}
        onChangeRowsPerPage={handleRowsPerPageChange}
        paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 30]}
        sortServer
        onSort={handleSort}
        subHeader
        subHeaderComponent={<CustomHeader search={inputValue} isfoc={focus} customSearch={adSearch} />}
        paginationComponentOptions={{ rowsPerPageText: t('Rows Per Page') }}
        progressPending={isLoading}
        progressComponent={
          <div className='imp-table-loader'>
            <span className='spinner-border spinner-border-sm text-primary me-2' />
            <span className='text-muted fs-7'>{t('Loading data...')}</span>
          </div>
        }
        noDataComponent={
          <div className='imp-empty-state'>
            <KTIcon iconName='chart-simple' className='fs-3x text-muted mb-3' />
            <div className='fw-bold fs-5 text-gray-600 mb-1'>{t('No Impression Data')}</div>
            <p className='text-muted fs-7'>{t('No impression reports found.')}</p>
          </div>
        }
      />

      {/* ── View Users Modal ── */}
      <Modal show={showUsersModal} onHide={closeUsersModal} centered size='lg' className='imp-modal-fixed'>
        <Modal.Header closeButton className='imp-modal-header'>
          <Modal.Title className='fw-bold fs-4 d-flex align-items-center gap-2'>
            {selectedUser ? (
              <>
                <button className='btn btn-sm btn-icon btn-light-primary rounded-circle' onClick={backToUsersList}>
                  <KTIcon iconName='arrow-left' className='fs-3' />
                </button>
                <KTIcon iconName='time' className='fs-2 text-primary' iconType='solid' />
                <span>{t('View History')} — {selectedUser.fullName || selectedUser.userName || ''}</span>
              </>
            ) : (
              <>
                <KTIcon iconName='people' className='fs-2 text-info' iconType='solid' />
                <span>{t('Ad Viewers')}</span>
              </>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          className='imp-modal-body'
          ref={selectedUser ? historyScrollRef : undefined}
        >
          {/* Ad summary */}
          {!selectedUser && selectedAd && (
            <div className='d-flex flex-wrap align-items-center gap-4 p-4 mb-4 rounded-3' style={{ background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
              <div className='flex-grow-1'>
                <div className='fw-bold fs-5 text-dark'>{selectedAd.name || selectedAd.en_name || '-'}</div>
                <div className='text-muted fs-8 mt-1'>
                  {selectedAd.fullName || '-'}{selectedAd.placementName ? ` · ${selectedAd.placementName}` : ''}
                </div>
              </div>
              <div className='d-flex gap-3'>
                {(selectedAd.totalImpressions || selectedAd.viewCount) && (
                  <div className='text-center px-3'>
                    <div className='fw-bolder fs-4 text-primary'>{(Number(selectedAd.totalImpressions || selectedAd.viewCount) || 0).toLocaleString()}</div>
                    <div className='text-muted fs-8'>{t('Impressions')}</div>
                  </div>
                )}
                {selectedAd.totalViews && (
                  <div className='text-center px-3' style={{ borderLeft: '1px solid #E2E8F0' }}>
                    <div className='fw-bolder fs-4 text-info'>{(Number(selectedAd.totalViews) || 0).toLocaleString()}</div>
                    <div className='text-muted fs-8'>{t('Views')}</div>
                  </div>
                )}
                {selectedAd.ctr && (
                  <div className='text-center px-3' style={{ borderLeft: '1px solid #E2E8F0' }}>
                    <div className='fw-bolder fs-4 text-success'>{selectedAd.ctr}%</div>
                    <div className='text-muted fs-8'>{t('CTR')}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Users List View ── */}
          {!selectedUser && (
            <>
              {usersLoading ? (
                <div className='imp-modal-loader'>
                  <div className='imp-skeleton-list'>
                    {[1, 2, 3, 4].map((i) => (
                      <div className='imp-skeleton-row' key={i}>
                        <div className='imp-skeleton imp-skeleton--circle' />
                        <div className='imp-skeleton-text'>
                          <div className='imp-skeleton imp-skeleton--line-lg' />
                          <div className='imp-skeleton imp-skeleton--line-sm' />
                        </div>
                        <div className='imp-skeleton imp-skeleton--badge' />
                      </div>
                    ))}
                  </div>
                </div>
              ) : usersList.length === 0 ? (
                <div className='imp-empty-state'>
                  <KTIcon iconName='people' className='fs-3x text-muted mb-3' />
                  <div className='fw-bold fs-5 text-gray-600 mb-1'>{t('No Viewers')}</div>
                  <p className='text-muted fs-7'>{t('No users have viewed this ad yet.')}</p>
                </div>
              ) : (
                <DataTable
                  columns={usersColumns}
                  data={usersList}
                  pagination
                  paginationServer
                  onChangePage={handleUsersPageChange}
                  paginationTotalRows={usersTotal}
                  paginationPerPage={usersLimit}
                  onChangeRowsPerPage={handleUsersRowsPerPageChange}
                  paginationRowsPerPageOptions={[5, 10, 15, 20]}
                  paginationComponentOptions={{ rowsPerPageText: t('Rows Per Page') }}
                  progressPending={usersLoading}
                  progressComponent={
                    <div className='imp-table-loader'>
                      <span className='spinner-border spinner-border-sm text-primary me-2' />
                      <span className='text-muted fs-7'>{t('Loading users...')}</span>
                    </div>
                  }
                />
              )}
            </>
          )}

          {/* ── User History View (Timeline with Infinite Scroll) ── */}
          {selectedUser && (
            <>
              {/* User summary card */}
              <div className='imp-user-summary'>
                {selectedUser.profile ? (
                  <img
                    src={imageBaseUrl + selectedUser.profile}
                    alt={selectedUser.fullName || ''}
                    className='imp-user-summary__img'
                    onError={(e) => {
                      e.currentTarget.onerror = null
                      e.currentTarget.src = toAbsoluteUrl('/media/avatars/blank.png')
                    }}
                  />
                ) : (
                  <div className='imp-user-summary__avatar'>
                    {(selectedUser.fullName || selectedUser.userName || 'U').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className='imp-user-summary__info'>
                  <div className='fw-bold fs-5'>{selectedUser.fullName || selectedUser.userName || '-'}</div>
                  {selectedUser.email && <div className='text-muted fs-7'>{selectedUser.email}</div>}
                </div>
                <div className='imp-user-summary__count'>
                  <span className='imp-badge imp-badge--count'>
                    <KTIcon iconName='eye' className='fs-5' />
                    {selectedUser.viewCount || selectedUser.count || 0} {t('total views')}
                  </span>
                </div>
              </div>

              {/* Initial loading skeleton */}
              {historyLoading ? (
                <div className='imp-timeline-skeleton'>
                  {[1, 2, 3].map((i) => (
                    <div className='imp-timeline-skeleton__item' key={i}>
                      <div className='imp-timeline-skeleton__dot' />
                      <div className='imp-timeline-skeleton__card'>
                        <div className='imp-skeleton imp-skeleton--line-lg' />
                        <div className='imp-skeleton imp-skeleton--line-sm' />
                        <div className='imp-skeleton imp-skeleton--line-md' />
                      </div>
                    </div>
                  ))}
                </div>
              ) : userHistory.length === 0 ? (
                <div className='imp-empty-state'>
                  <KTIcon iconName='time' className='fs-3x text-muted mb-3' />
                  <div className='fw-bold fs-5 text-gray-600 mb-1'>{t('No History')}</div>
                  <p className='text-muted fs-7'>{t('No view history available for this user.')}</p>
                </div>
              ) : (
                <>
                  {/* Timeline */}
                  <div className='imp-timeline'>
                    {userHistory.map((entry: any, idx: number) => {
                      const isLast = idx === userHistory.length - 1 && !historyHasMore
                      return (
                        <div className={`imp-timeline__item ${isLast ? 'imp-timeline__item--last' : ''}`} key={idx}>
                          <div className='imp-timeline__dot-col'>
                            <div className='imp-timeline__dot' />
                            {!isLast && <div className='imp-timeline__line' />}
                          </div>
                          <div className='imp-timeline__content'>
                            <div className='imp-timeline__card'>
                              <div className='imp-timeline__card-header'>
                                <KTIcon iconName='eye' className='fs-4 text-primary' />
                                <span className='fw-bold fs-7'>{t('Viewed Ad')}</span>
                              </div>
                              <div className='imp-timeline__card-body'>
                                <div className='row g-2'>
                                  {(entry.viewedAt || entry.created) && (
                                    <div className='col-sm-6'>
                                      <div className='text-muted fs-8'>{t('Date & Time')}</div>
                                      <div className='fw-semibold fs-7'>
                                        {formatDate(entry.viewedAt || entry.created, 'DD-MM-YYYY')}
                                        <span className='text-muted ms-1'>
                                          {new Date(entry.viewedAt || entry.created).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                 
                               
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {/* Loading more indicator */}
                  {historyLoadingMore && (
                    <div className='imp-load-more'>
                      <span className='spinner-border spinner-border-sm text-primary me-2' />
                      <span className='text-muted fs-7'>{t('Loading more...')}</span>
                    </div>
                  )}

                  {/* Load more button (visible when has more and not currently loading) */}
                  {historyHasMore && !historyLoadingMore && (
                    <div className='imp-load-more'>
                      <button
                        className='btn btn-light-primary btn-sm d-flex align-items-center gap-2 px-4 py-2'
                        onClick={loadMoreHistory}
                      >
                        <KTIcon iconName='arrow-down' className='fs-4' />
                        {t('Load More')}
                      </button>
                    </div>
                  )}

                  {/* End of list indicator */}
                  {!historyHasMore && userHistory.length > 0 && (
                    <div className='imp-end-of-list'>
                      <div className='imp-end-of-list__line' />
                      <span className='text-muted fs-8'>{t('All history loaded')}</span>
                      <div className='imp-end-of-list__line' />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer className='imp-modal-footer'>
          <button className='btn btn-light btn-sm px-5' onClick={closeUsersModal}>
            {t('Close')}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export { AdsImpressions }
